import { NgClass } from '@angular/common';
import { Component, effect, Inject, Input, input, output, QueryList, ViewChild, viewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import {
  CharityStore,
  Day,
  days,
  ENVIRONMENT,
  Environment,
  halfHours,
  hours,
  Schedule,
  ScheduleLeadTime,
  ScheduleMeta,
  Zone,
} from '@domains';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { MapComponent } from '@rspl-map';
import {
  CardComponent,
  ConfirmDialogComponent,
  DisableDoubleClickDirective,
  FxFlexDirective,
  FxLayoutAlignDirective,
  FxLayoutDirective,
  FxLayoutGapDirective,
  InfoBoxComponent,
  Responsive,
  ResponsiveService,
} from '@rspl-ui';
import * as moment from 'moment';
import { take, takeUntil } from 'rxjs/operators';

import { ZoneLookupComponent } from '../../../../../../ui/src/lib/lookup/zone-lookup/zone-lookup.component';
import { ScheduleConfig } from '../../../components/schedule/schedule-config';
import { ZipPickerComponent } from '../../../components/zip-picker/zip-picker.component';
import { AppState } from '../../../store/app-state.interface';
import { MultipleZoneDialogComponent } from '../multiple-zone-dialog/multiple-zone-dialog.component';
import { SaveZoneDialogComponent } from '../save-zone-dialog/save-zone-dialog.component';

type TimeType = FormGroup<{
  closed: FormControl<boolean>;
  opens: FormControl<string>;
  closes: FormControl<string>;
}>;

type ZoneType = FormGroup<{
  amZips: FormControl<string[]>;
  pmZips: FormControl<string[]>;
}>;

type CapacityType = FormGroup<{
  amCapacity: FormControl<number>;
  pmCapacity: FormControl<number>;
}>;

@Component({
  selector: 'app-schedule-form',
  templateUrl: './schedule-form.component.html',
  styleUrls: ['./schedule-form.component.scss'],
  imports: [
    FormsModule,
    FxLayoutDirective,
    FxLayoutGapDirective,
    ReactiveFormsModule,
    MatLabel,
    MatButtonToggleGroup,
    MatButtonToggle,
    InfoBoxComponent,
    CardComponent,
    FxFlexDirective,
    NgClass,
    MatSlideToggle,
    MatFormField,
    MatSelect,
    MatOption,
    FxLayoutAlignDirective,
    MatInput,
    MatButton,
    MatTooltip,
    ZipPickerComponent,
    DisableDoubleClickDirective,
    MapComponent,
    ZoneLookupComponent,
  ],
})
export class ScheduleFormComponent extends Responsive {
  isCaptain;
  #config!: ScheduleConfig;
  readonly zoneLookup = viewChild<ZoneLookupComponent>('ZoneLookup');
  @Input() showWarning = true;
  @Input() showMap = true;
  @Input() day!: Day;
  updateSavedZones = output();

  @Input() set config(config: ScheduleConfig) {
    this.#config = config;
    if (this.config) {
      this.init();
    }
  }

  get config(): ScheduleConfig {
    return this.#config;
  }

  isDefault = input(true);
  defaultSchedule = input<Schedule>();
  @Input() year?: number;
  @Input() week?: number;
  isVisible = input<boolean>(true);
  readonly scheduleSaved = output();

  #zipPickerComponent!: MapComponent;
  @ViewChild(MapComponent) set zipPickerComponent(zipPickerComponent: MapComponent) {
    this.#zipPickerComponent = zipPickerComponent;
    if (this.zipPickerComponent && this.config.address) {
      this.zipPickerComponent.setAddress(this.config.address, false);
    }
  }

  get zipPickerComponent(): MapComponent {
    return this.#zipPickerComponent;
  }

  #zoneComponents!: QueryList<ZipPickerComponent>;
  @ViewChildren('zone') set zoneComponents(zoneComponents: QueryList<ZipPickerComponent>) {
    this.#zoneComponents = zoneComponents;
    if (this.zoneComponents && this.schedule) {
      this.patchZones();
    }
  }

  get zoneComponents(): QueryList<ZipPickerComponent> {
    return this.#zoneComponents;
  }

  parentId: string;
  partnerId: string;
  charityId: string;
  schedule!: Schedule;
  form!: FormGroup<{
    id: FormControl<string>;
    week: FormControl<number>;
    year: FormControl<number>;
    mondayTime: TimeType;
    mondayCapacity: CapacityType;
    mondayZone: ZoneType;
    tuesdayTime: TimeType;
    tuesdayCapacity: CapacityType;
    tuesdayZone: ZoneType;
    wednesdayTime: TimeType;
    wednesdayCapacity: CapacityType;
    wednesdayZone: ZoneType;
    thursdayTime: TimeType;
    thursdayCapacity: CapacityType;
    thursdayZone: ZoneType;
    fridayTime: TimeType;
    fridayCapacity: CapacityType;
    fridayZone: ZoneType;
    saturdayTime: TimeType;
    saturdayCapacity: CapacityType;
    saturdayZone: ZoneType;
    sundayTime: TimeType;
    sundayCapacity: CapacityType;
    sundayZone: ZoneType;
    leadTime?: FormControl<ScheduleLeadTime>;
    meta: FormControl<ScheduleMeta>;
  }>;
  days = days;
  selectedDay: Day = this.days[0];
  fetched = false;
  weekMissing = false;
  zones?: Zone[];
  filteredZones?: Zone[];

  hasChanged = false;

  leadTimes = [ScheduleLeadTime.H24, ScheduleLeadTime.H48, ScheduleLeadTime.H72];

  selectedZone?: Zone;

  constructor(
    private route: ActivatedRoute,
    private store: Store<AppState>,
    private actions: Actions,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    public override responsiveService: ResponsiveService,
    @Inject(ENVIRONMENT) protected environment: Environment,
  ) {
    super(responsiveService);
    this.isCaptain = environment.isCaptain;
    this.parentId = this.route.snapshot.params['id'];
    this.partnerId = this.route.snapshot.params['partnerId'];
    this.charityId = this.route.snapshot.params['charityId'];
    effect(() => {
      if (this.defaultSchedule() && !this.isDefault()) {
        this.checkIfChanged();
      }
    });
    effect(() => {
      if (!this.isVisible()) {
        this.selectedZone = undefined;
      }
    });
  }

  init(): void {
    this.parentId = this.config.parentId || this.parentId;
    this.partnerId = this.config.partnerId || this.partnerId;
    this.charityId = this.config.charityId || this.charityId;
    this.store.dispatch(
      this.config.findAction({
        parentId: this.parentId,
        findParams: {
          page: 1,
          per_page: 1,
          ...(this.isDefault() ? { default: true } : {}),
          ...(!this.isDefault() ? { selected: this.week + '-' + this.year } : {}),
        },
      }),
    );
    this.actions.pipe(ofType(this.config.findActionSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.fetched = true;
      this.form?.enable();
    });
    this.store.pipe(select(this.config.scheduleSelector), takeUntil(this.destroy$)).subscribe((schedule) => {
      if (schedule?.length > 0 || this.isDefault()) {
        this.selectedDay = this.day || this.days[0];
        if (!this.isDefault() && !(schedule[0].week && schedule[0].year)) {
          this.weekMissing = true;
        }
        this.schedule = schedule[0];
        this.initForm();
        if (!this.schedule) {
          this.schedule = new Schedule();
        } else if (this.config.parentType === 'Truck') {
          this.schedule = new Schedule({
            ...this.schedule,
            leadTime: this.schedule.leadTime || ScheduleLeadTime.H48,
          });
        }
        this.patchForm();
        this.patchZones();
        this.checkIfChanged();
      }
    });
    this.actions.pipe(ofType(this.config.createActionSuccess, this.config.updateActionSuccess), takeUntil(this.destroy$)).subscribe(() => {
      if (this.isDefault()) {
        this.config.defaultCreatedSuccess && this.config.defaultCreatedSuccess();
      } else {
        this.store.dispatch(
          this.config.findAction({
            parentId: this.parentId,
            findParams: {
              page: 1,
              per_page: 1,
              ...(this.isDefault() ? { default: true } : {}),
              ...(!this.isDefault() ? { selected: this.week + '-' + this.year } : {}),
            },
          }),
        );
      }
      this.snackBar.open(this.config.parentType + ' Schedule saved successfully!', undefined, {
        duration: 5000,
        panelClass: 'success',
      });
    });
  }

  private initForm(): void {
    this.form = new FormGroup({
      id: new FormControl<string>(''),
      week: new FormControl(this.week),
      year: new FormControl(this.year),
      mondayTime: this.createTimeGroup(),
      mondayCapacity: this.createCapacityGroup(),
      mondayZone: this.createZoneGroup(),
      tuesdayTime: this.createTimeGroup(),
      tuesdayCapacity: this.createCapacityGroup(),
      tuesdayZone: this.createZoneGroup(),
      wednesdayTime: this.createTimeGroup(),
      wednesdayCapacity: this.createCapacityGroup(),
      wednesdayZone: this.createZoneGroup(),
      thursdayTime: this.createTimeGroup(),
      thursdayCapacity: this.createCapacityGroup(),
      thursdayZone: this.createZoneGroup(),
      fridayTime: this.createTimeGroup(),
      fridayCapacity: this.createCapacityGroup(),
      fridayZone: this.createZoneGroup(),
      saturdayTime: this.createTimeGroup(),
      saturdayCapacity: this.createCapacityGroup(),
      saturdayZone: this.createZoneGroup(),
      sundayTime: this.createTimeGroup(),
      sundayCapacity: this.createCapacityGroup(),
      sundayZone: this.createZoneGroup(),
      meta: new FormControl<ScheduleMeta>({}),
    });
    if (this.config.parentType === 'Truck') this.form.addControl('leadTime', new FormControl());
  }

  createTimeGroup(): TimeType {
    return new FormGroup({
      closed: new FormControl(false),
      opens: new FormControl(null),
      closes: new FormControl(null),
    });
  }

  private createZoneGroup(): ZoneType {
    return new FormGroup({
      amZips: new FormControl(null, Validators.required),
      pmZips: new FormControl(null, Validators.required),
    });
  }

  private createCapacityGroup(): CapacityType {
    return new FormGroup({
      amCapacity: new FormControl(this.charityStoreParent?.meta?.capacity ? 9999 : null),
      pmCapacity: new FormControl(this.charityStoreParent?.meta?.capacity ? 9999 : null),
    });
  }

  private patchForm(): void {
    this.form.patchValue({
      id: this.weekMissing ? null : this.schedule?.id,
      mondayTime: {
        closed: this.schedule?.mondayTime?.closed,
        opens: this.schedule?.mondayTime?.opens,
        closes: this.schedule?.mondayTime?.closes,
      },
      mondayCapacity: {
        amCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.mondayCapacity?.amCapacity || 0,
        pmCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.mondayCapacity?.pmCapacity || 0,
      },
      mondayZone: {
        amZips: this.schedule?.mondayZone?.amZips || [],
        pmZips: this.schedule?.mondayZone?.pmZips || [],
      },
      tuesdayTime: {
        closed: this.schedule?.tuesdayTime?.closed,
        opens: this.schedule?.tuesdayTime?.opens,
        closes: this.schedule?.tuesdayTime?.closes,
      },
      tuesdayCapacity: {
        amCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.tuesdayCapacity?.amCapacity || 0,
        pmCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.tuesdayCapacity?.pmCapacity || 0,
      },
      tuesdayZone: {
        amZips: this.schedule?.tuesdayZone?.amZips || [],
        pmZips: this.schedule?.tuesdayZone?.pmZips || [],
      },
      wednesdayTime: {
        closed: this.schedule?.wednesdayTime?.closed,
        opens: this.schedule?.wednesdayTime?.opens,
        closes: this.schedule?.wednesdayTime?.closes,
      },
      wednesdayCapacity: {
        amCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.wednesdayCapacity?.amCapacity || 0,
        pmCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.wednesdayCapacity?.pmCapacity || 0,
      },
      wednesdayZone: {
        amZips: this.schedule?.wednesdayZone?.amZips || [],
        pmZips: this.schedule?.wednesdayZone?.pmZips || [],
      },
      thursdayTime: {
        closed: this.schedule?.thursdayTime?.closed,
        opens: this.schedule?.thursdayTime?.opens,
        closes: this.schedule?.thursdayTime?.closes,
      },
      thursdayCapacity: {
        amCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.thursdayCapacity?.amCapacity || 0,
        pmCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.thursdayCapacity?.pmCapacity || 0,
      },
      thursdayZone: {
        amZips: this.schedule?.thursdayZone?.amZips || [],
        pmZips: this.schedule?.thursdayZone?.pmZips || [],
      },
      fridayTime: {
        closed: this.schedule?.fridayTime?.closed,
        opens: this.schedule?.fridayTime?.opens,
        closes: this.schedule?.fridayTime?.closes,
      },
      fridayCapacity: {
        amCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.fridayCapacity?.amCapacity || 0,
        pmCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.fridayCapacity?.pmCapacity || 0,
      },
      fridayZone: {
        amZips: this.schedule?.fridayZone?.amZips || [],
        pmZips: this.schedule?.fridayZone?.pmZips || [],
      },
      saturdayTime: {
        closed: this.schedule?.saturdayTime?.closed,
        opens: this.schedule?.saturdayTime?.opens,
        closes: this.schedule?.saturdayTime?.closes,
      },
      saturdayCapacity: {
        amCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.saturdayCapacity?.amCapacity || 0,
        pmCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.saturdayCapacity?.pmCapacity || 0,
      },
      saturdayZone: {
        amZips: this.schedule?.saturdayZone?.amZips || [],
        pmZips: this.schedule?.saturdayZone?.pmZips || [],
      },
      sundayTime: {
        closed: this.schedule?.sundayTime?.closed,
        opens: this.schedule?.sundayTime?.opens,
        closes: this.schedule?.sundayTime?.closes,
      },
      sundayCapacity: {
        amCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.sundayCapacity?.amCapacity || 0,
        pmCapacity: this.charityStoreParent?.meta?.capacity ? 9999 : this.schedule?.sundayCapacity?.pmCapacity || 0,
      },
      sundayZone: {
        amZips: this.schedule?.sundayZone?.amZips || [],
        pmZips: this.schedule?.sundayZone?.pmZips || [],
      },
      meta: this.schedule.meta,
    });
    for (const item of this.days) {
      this.dayToggle(item, this.schedule[item + 'Time'].closed || false);
      if (!this.selectedDay && !this.schedule[item + 'Time'].closed) {
        this.selectDay(item);
      }
    }
    if (this.zoneComponents && this.schedule) {
      for (let i = 0; i < this.zoneComponents?.length; i++) {
        this.zoneComponents.get(i).zips = [...this.schedule[this.days[i] + 'Zone'].amZips];
      }
    }
    if (this.selectedDay) {
      this.updatedZips(this.schedule[this.selectedDay + 'Zone']?.amZips);
    }
    if (this.config.parentType === 'Truck') this.leadTime.patchValue(this.schedule.leadTime);
  }

  get leadTime(): FormControl {
    return this.form?.get('leadTime') as FormControl;
  }

  setLeadTime(leadTime: ScheduleLeadTime) {
    this.leadTime.setValue(leadTime);
  }

  getTimeGroup(name): TimeType {
    return this.form.get(name) as TimeType;
  }

  getZoneGroup(name): ZoneType {
    return this.form.get(name) as ZoneType;
  }

  getCapacityGroup(name): CapacityType {
    return this.form.get(name) as CapacityType;
  }

  getClosedControl(group: string): FormControl<boolean> {
    return this.getTimeGroup(group).get('closed') as FormControl<boolean>;
  }

  getAmCapacityControl(group: string): FormControl<number> {
    return this.getCapacityGroup(group).get('amCapacity') as FormControl<number>;
  }

  getPmCapacityControl(group: string): FormControl<number> {
    return this.getCapacityGroup(group).get('pmCapacity') as FormControl<number>;
  }

  getOpensControl(group: string): FormControl<string> {
    return this.getTimeGroup(group).get('opens') as FormControl<string>;
  }

  getClosesControl(group: string): FormControl<string> {
    return this.getTimeGroup(group).get('closes') as FormControl<string>;
  }

  getTitle(day: Day | string): string {
    let date;
    if (!this.isDefault() && this.week && this.year) {
      date = moment()
        .date(1)
        .month(0)
        .year(this.year)
        .add(this.week - 1, 'weeks')
        .startOf('week');
    }
    return day
      ? this.isDefault()
        ? day[0].toUpperCase() + day.slice(1)
        : date
            ?.clone()
            .add(this.days.indexOf(day as Day), 'days')
            .format('ddd MMM DD, y') || ''
      : '';
  }

  submitForm(): void {
    if (this.form.invalid) {
      return;
    }
    if (this.schedule?.id && !this.weekMissing) {
      this.store.dispatch(
        this.config.updateAction({
          parentId: this.parentId,
          schedule: new Schedule(this.form.getRawValue()),
        }),
      );
    } else {
      this.store.dispatch(
        this.config.createAction({
          parentId: this.parentId,
          schedule: new Schedule(this.form.getRawValue()),
        }),
      );
    }
  }

  updatedZips(zips: string[]): void {
    if (this.config.zonesPerDay) {
      if (!this.areZipsEqual(zips, this.getZoneGroup(this.selectedDay + 'Zone').get('amZips').value)) {
        this.updateMetaZone(this.selectedDay);
      }
      this.getZoneGroup(this.selectedDay + 'Zone')
        .get('amZips')
        .patchValue(zips);
      this.getZoneGroup(this.selectedDay + 'Zone')
        .get('pmZips')
        .patchValue(zips);
      if (this.zoneComponents?.get(this.days?.indexOf(this.selectedDay))) {
        this.zoneComponents.get(this.days.indexOf(this.selectedDay)).zips = [...zips];
      }
    } else {
      this.days.forEach((day, i) => {
        if (!this.areZipsEqual(zips, this.getZoneGroup(day + 'Zone').get('amZips').value)) {
          this.updateMetaZone(day);
        }
        this.getZoneGroup(day + 'Zone')
          .get('amZips')
          .patchValue(zips);
        this.getZoneGroup(day + 'Zone')
          .get('pmZips')
          .patchValue(zips);
        if (this.zoneComponents?.get(i)) {
          this.zoneComponents.get(i).zips = [...zips];
        }
      });
    }
    if (!this.areZipsEqual(zips, this.selectedZone?.zips || [])) {
      this.zoneLookup()?.clear();
    }
  }

  getDayZips(day: string): string[] {
    return this.getZoneGroup(day + 'Zone')?.get('amZips')?.value;
  }

  dayToggle(day: string, closed: boolean): void {
    if (!closed) {
      this.getZoneGroup(day + 'Zone')
        .get('amZips')
        .setValidators(Validators.required);
      this.getZoneGroup(day + 'Zone')
        .get('pmZips')
        .setValidators(Validators.required);
    } else {
      this.getZoneGroup(day + 'Zone')
        .get('amZips')
        .clearValidators();
      this.getZoneGroup(day + 'Zone')
        .get('pmZips')
        .clearValidators();
      if (this.selectedDay === day) {
        this.selectedDay = this.days.find(() => !this.getTimeGroup(day + 'Time').get('closed').value);
      }
    }
    this.getZoneGroup(day + 'Zone')
      .get('amZips')
      .updateValueAndValidity();
    this.getZoneGroup(day + 'Zone')
      .get('pmZips')
      .updateValueAndValidity();
    this.getClosedControl(day + 'Time').patchValue(closed);
    if (closed) {
      this.getAmCapacityControl(day + 'Capacity').disable();
      this.getAmCapacityControl(day + 'Capacity').clearValidators();

      this.getPmCapacityControl(day + 'Capacity').disable();
      this.getPmCapacityControl(day + 'Capacity').clearValidators();

      this.getOpensControl(day + 'Time').disable();
      this.getOpensControl(day + 'Time').clearValidators();

      this.getClosesControl(day + 'Time').disable();
      this.getClosesControl(day + 'Time').clearValidators();
    } else {
      this.getAmCapacityControl(day + 'Capacity').enable();
      this.getAmCapacityControl(day + 'Capacity').setValidators(Validators.required);

      this.getPmCapacityControl(day + 'Capacity').enable();
      this.getPmCapacityControl(day + 'Capacity').setValidators(Validators.required);

      this.getOpensControl(day + 'Time').enable();
      this.getOpensControl(day + 'Time').setValidators(Validators.required);

      this.getClosesControl(day + 'Time').enable();
      this.getClosesControl(day + 'Time').setValidators(Validators.required);
    }
    this.getAmCapacityControl(day + 'Capacity').updateValueAndValidity();
    this.getPmCapacityControl(day + 'Capacity').updateValueAndValidity();
    this.getOpensControl(day + 'Time').updateValueAndValidity();
    this.getClosesControl(day + 'Time').updateValueAndValidity();
  }

  getZips(day: Day): string[] {
    const zips = this.schedule ? this.schedule[day + 'Zone'].amZips : [];
    return zips;
  }

  selectDay(day: Day | string): void {
    this.selectedDay = day as Day;
    this.selectedZone = undefined;
    if (this.zoneLookup()) {
      this.zoneLookup().itemFetched = false;
    }
  }

  fetchData(week?: number, year?: number): void {
    this.week = week || this.week;
    this.year = year || this.year;
    this.weekMissing = false;
    this.fetched = false;
    this.form.disable();
    this.store.dispatch(
      this.config.findAction({
        parentId: this.parentId,
        findParams: {
          page: 1,
          per_page: 1,
          ...(!week && !year ? { default: true } : {}),
          ...(week && year ? { selected: week + '-' + year } : {}),
        },
      }),
    );
  }

  get showDays(): string[] {
    return this.day ? [this.day] : this.days;
  }

  private patchZones(): void {
    if (this.day && this.zoneComponents.get(0)) {
      this.zoneComponents.get(0).zips = [...this.schedule[this.day + 'Zone'].amZips];
    } else {
      for (let i = 0; i < this.zoneComponents?.length; i++) {
        if (this.zoneComponents.get(i) && this.schedule[this.days[i] + 'Zone'])
          this.zoneComponents.get(i).zips = [...this.schedule[this.days[i] + 'Zone'].amZips];
      }
    }
  }

  setZone(zone?: Zone) {
    this.selectedZone = zone;
    if (zone) this.updatedZips(zone?.zips);
    if (this.config.zonesPerDay) {
      this.updateMetaZone(this.selectedDay, zone);
    } else {
      this.days.forEach((day) => this.updateMetaZone(day, zone));
    }
  }

  saveZone(): void {
    const reference = this.dialog.open(SaveZoneDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {
        title: 'Create Zone',
        parentId: this.parentId,
        address: this.config.address,
        zone: new Zone({
          zips: this.getZoneGroup(this.selectedDay + 'Zone').get('amZips').value,
          ...(this.charityId
            ? {
                charityId: this.charityId,
                store_id: this.parentId,
              }
            : {}),
          ...(this.partnerId
            ? {
                partnerId: this.partnerId,
                vehicle_id: this.parentId,
              }
            : {}),
          template_type: 'custom',
        }),
      },
    });
    reference.afterClosed().subscribe((result) => {
      if (result) {
        if (this.config.zonesPerDay) {
          this.updateMetaZone(this.selectedDay, result);
        } else {
          this.days.forEach((day) => this.updateMetaZone(day, result));
        }
      }
    });
  }

  private updateMetaZone(day: Day, zone?: Zone) {
    const meta = new ScheduleMeta({ ...this.form.get('meta').value });
    meta[day + '_zone_id'] = zone?.id || null;
    this.form.patchValue({ meta });
  }

  get hours(): { value: string; viewValue: string }[] {
    return this.config.halfHourIntervals ? halfHours : hours;
  }

  assignMultipleZones() {
    const reference = this.dialog.open(MultipleZoneDialogComponent, {
      width: '700px',
      maxWidth: '90vw',
      data: {
        schedule: this.schedule,
        address: this.config.address,
        parentId: this.parentId,
      },
    });
    reference.afterClosed().subscribe((result) => {
      if (result) {
        if (result.updateSavedZones) {
          this.updateSavedZones.emit();
        }
      }
    });
  }

  areZipsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }

  get charityStoreParent(): CharityStore {
    return this.config.parent as CharityStore;
  }

  checkIfChanged() {
    this.hasChanged = false;
    if (!this.isDefault() && this.defaultSchedule() && this.schedule?.id) {
      if (
        this.defaultSchedule().id !== this.schedule.id &&
        (!this.areZipsEqual(this.defaultSchedule().mondayZone.amZips, this.schedule.mondayZone.amZips) ||
          !this.areZipsEqual(this.defaultSchedule().mondayZone.pmZips, this.schedule.mondayZone.pmZips) ||
          !this.areZipsEqual(this.defaultSchedule().tuesdayZone.amZips, this.schedule.tuesdayZone.amZips) ||
          !this.areZipsEqual(this.defaultSchedule().tuesdayZone.pmZips, this.schedule.tuesdayZone.pmZips) ||
          !this.areZipsEqual(this.defaultSchedule().wednesdayZone.amZips, this.schedule.wednesdayZone.amZips) ||
          !this.areZipsEqual(this.defaultSchedule().wednesdayZone.pmZips, this.schedule.wednesdayZone.pmZips) ||
          !this.areZipsEqual(this.defaultSchedule().thursdayZone.amZips, this.schedule.thursdayZone.amZips) ||
          !this.areZipsEqual(this.defaultSchedule().thursdayZone.pmZips, this.schedule.thursdayZone.pmZips) ||
          !this.areZipsEqual(this.defaultSchedule().fridayZone.amZips, this.schedule.fridayZone.amZips) ||
          !this.areZipsEqual(this.defaultSchedule().fridayZone.pmZips, this.schedule.fridayZone.pmZips) ||
          !this.areZipsEqual(this.defaultSchedule().saturdayZone.amZips, this.schedule.saturdayZone.amZips) ||
          !this.areZipsEqual(this.defaultSchedule().saturdayZone.pmZips, this.schedule.saturdayZone.pmZips) ||
          !this.areZipsEqual(this.defaultSchedule().sundayZone.amZips, this.schedule.sundayZone.amZips) ||
          !this.areZipsEqual(this.defaultSchedule().sundayZone.pmZips, this.schedule.sundayZone.pmZips))
      ) {
        this.hasChanged = true;
        if (moment(this.defaultSchedule().updatedAt).isAfter(moment(this.schedule.updatedAt))) {
          this.snackBar.open('Default zone changed! Your default zone has been changed since you created this weekly schedule', 'x', {
            duration: 10000,
            panelClass: 'orange',
          });
        }
      }
    }
  }

  matchToDefault() {
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Are you sure?',
          yes: 'Yes',
          no: 'No',
          color: 'primary',
          message: 'Are you sure you want to update this weekly schedule to match the default one?',
        },
        width: '500px',
        maxWidth: '90vw',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          const schedule = new Schedule(this.schedule);
          schedule.meta.monday_zone_id = this.defaultSchedule().meta.monday_zone_id;
          schedule.mondayZone.amZips = this.defaultSchedule().mondayZone.amZips;
          schedule.mondayZone.pmZips = this.defaultSchedule().mondayZone.pmZips;
          schedule.meta.tuesday_zone_id = this.defaultSchedule().meta.tuesday_zone_id;
          schedule.tuesdayZone.amZips = this.defaultSchedule().tuesdayZone.amZips;
          schedule.tuesdayZone.pmZips = this.defaultSchedule().tuesdayZone.pmZips;
          schedule.meta.wednesday_zone_id = this.defaultSchedule().meta.wednesday_zone_id;
          schedule.wednesdayZone.amZips = this.defaultSchedule().wednesdayZone.amZips;
          schedule.wednesdayZone.pmZips = this.defaultSchedule().wednesdayZone.pmZips;
          schedule.meta.thursday_zone_id = this.defaultSchedule().meta.thursday_zone_id;
          schedule.thursdayZone.amZips = this.defaultSchedule().thursdayZone.amZips;
          schedule.thursdayZone.pmZips = this.defaultSchedule().thursdayZone.pmZips;
          schedule.meta.friday_zone_id = this.defaultSchedule().meta.friday_zone_id;
          schedule.fridayZone.amZips = this.defaultSchedule().fridayZone.amZips;
          schedule.fridayZone.pmZips = this.defaultSchedule().fridayZone.pmZips;
          schedule.meta.saturday_zone_id = this.defaultSchedule().meta.saturday_zone_id;
          schedule.saturdayZone.amZips = this.defaultSchedule().saturdayZone.amZips;
          schedule.saturdayZone.pmZips = this.defaultSchedule().saturdayZone.pmZips;
          schedule.meta.sunday_zone_id = this.defaultSchedule().meta.sunday_zone_id;
          schedule.sundayZone.amZips = this.defaultSchedule().sundayZone.amZips;
          schedule.sundayZone.pmZips = this.defaultSchedule().sundayZone.pmZips;
          this.schedule = schedule;
          this.store.dispatch(this.config.updateAction({ parentId: this.parentId, schedule: schedule }));
        }
      });
  }
}
